import React from "react";
import { X } from "lucide-react";
import BookmarkGroupsBrowser from "./BookmarkGroupsBrowser";
import type { BookmarkGroupData } from "../utils/bookmarkGroupData";
import {
  MODAL_ANIMATION_CSS,
  MODAL_OVERLAY_STYLE,
  MODAL_PANEL_STYLE,
  useBodyScrollLock,
} from "./modalEffects";

type BookmarkModalProps = {
  isOpen: boolean;
  onClose: () => void;
  preferredBookType?: "adults" | "kids" | null;
  activeGroupName: string;
  selectedBookId: string | null;
  bookmarkGroups: BookmarkGroupData[];
  onCreateGroup: (bookId: string, groupName: string) => Promise<void> | void;
  isCreatingGroup: boolean;
  onOpenSavedBookmark: (bookmark: ElectronicBookmark) => void;
  isLoadingBookmarks: boolean;
  onClearMessage: () => void;
  bookmarkMessage: {
    type: "success" | "error" | "info" | "warning";
    text: string;
  } | null;
};

const BookmarkModal: React.FC<BookmarkModalProps> = ({
  isOpen,
  onClose,
  preferredBookType,
  activeGroupName,
  selectedBookId,
  bookmarkGroups,
  onCreateGroup,
  isCreatingGroup,
  onOpenSavedBookmark,
  isLoadingBookmarks,
  onClearMessage,
  bookmarkMessage,
}) => {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 px-4 py-6 backdrop-blur-sm"
      style={MODAL_OVERLAY_STYLE}
    >
      <style>{MODAL_ANIMATION_CSS}</style>
      <div
        className="relative flex h-[92vh] max-h-[760px] w-full max-w-5xl flex-col overflow-hidden rounded-[30px] border border-white/35 bg-[linear-gradient(180deg,rgba(255,252,248,0.98),rgba(248,250,252,0.98))] shadow-[0_40px_120px_rgba(15,23,42,0.24)]"
        style={MODAL_PANEL_STYLE}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white/90 text-slate-700 shadow-sm transition hover:bg-white"
          aria-label="Zamknij"
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>

        <BookmarkGroupsBrowser
          preferredBookType={preferredBookType}
          activeGroupName={activeGroupName}
          selectedBookId={selectedBookId}
          bookmarkGroups={bookmarkGroups}
          onCreateGroup={onCreateGroup}
          isCreatingGroup={isCreatingGroup}
          onOpenSavedBookmark={onOpenSavedBookmark}
          isLoadingBookmarks={isLoadingBookmarks}
          onClearMessage={onClearMessage}
          bookmarkMessage={bookmarkMessage}
          showBookmarkIcon={false}
          showBookmarkColumnAccentBackground
          className="h-full min-h-0 flex-1 lg:grid-cols-[280px,minmax(0,1fr)]"
          createGroupControlsPlacement="bottom"
          actionButtonStyleVariant="modal"
          bookTypeToggleStyleVariant="admin"
        />
      </div>
    </div>
  );
};

export default BookmarkModal;
